/**
 * Связи карточки договора со складом (поток 5, запросы в docs/streams.md):
 * - «Поставки» (СК-05): спецификация расходного договора против полученного на склады, недопоставка и поступления;
 * - «Материалы заказчика» (СК-25): склады заказчика по доходному договору — получено, выдано в работу, остаток.
 * Данные читаются с адресов склада; без права на склад или договоры адрес отвечает 404 — блок не показывается.
 */
import type { ReportView, Warehouse } from '@epc/contracts';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ApiError, api } from '../../lib/api';
import { date } from '../../lib/format';
import { Badge } from '../../ui/feedback';
import { Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { warehousesQuery } from '../warehouse/queries';

const fmt = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });

/** Ответ GET /warehouse/contracts/:id/deliveries (схема в apps/api/src/modules/warehouse/links.ts) */
interface ContractDeliveries {
  contractId: string;
  number: string;
  items: Array<{ name: string; unit: string; contracted: number; received: number; shortfall: number }>;
  extra: Array<{ name: string; unit: string; received: number }>;
  receipts: Array<{
    id: string;
    number: string;
    docDate: string;
    warehouseName: string;
    status: string;
    lines: number;
  }>;
  complete: boolean;
}

const hidden = (err: unknown) => err instanceof ApiError && (err.status === 403 || err.status === 404);

export function ContractDeliveriesPanel({ contractId }: { contractId: string }) {
  const q = useQuery({
    queryKey: ['wh', 'contract-deliveries', contractId],
    queryFn: () => api<ContractDeliveries>(`/warehouse/contracts/${contractId}/deliveries`),
    retry: false,
  });
  if (q.isPending || (q.isError && hidden(q.error))) return null;
  if (q.isError) return null;
  const d = q.data;
  if (!d.items.length && !d.receipts.length) return null;
  return (
    <Panel
      title="Поставки на склад"
      meta={d.complete ? 'спецификация получена полностью' : 'по проведённым поступлениям'}
      bodyClassName="p-0"
    >
      <ul className="divide-y divide-line md:hidden">
        {d.items.map((i) => (
          <li key={`${i.name}|${i.unit}`} className="flex flex-col gap-1 px-4 py-3 text-[13px]">
            <span className="text-[14px] text-ink">{i.name}</span>
            <span className="num flex flex-wrap justify-between gap-x-3 text-ink-2">
              <span>
                получено {fmt.format(i.received)} из {fmt.format(i.contracted)} {i.unit}
              </span>
              {i.shortfall > 0 ? (
                <span className="text-danger">не хватает {fmt.format(i.shortfall)}</span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      <ScrollX className="hidden md:block">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[12px] text-ink-2">
              <th className="px-4 py-2 font-medium">Позиция</th>
              <th className="px-3 py-2 text-right font-medium">По договору</th>
              <th className="px-3 py-2 text-right font-medium">Получено</th>
              <th className="px-4 py-2 text-right font-medium">Не хватает</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {d.items.map((i) => (
              <tr key={`${i.name}|${i.unit}`}>
                <td className="px-4 py-2 text-ink">{i.name}</td>
                <td className="num px-3 py-2 text-right text-ink-2">
                  {fmt.format(i.contracted)} {i.unit}
                </td>
                <td className="num px-3 py-2 text-right text-ink">{fmt.format(i.received)}</td>
                <td className="num px-4 py-2 text-right">
                  {i.shortfall > 0 ? <span className="text-danger">{fmt.format(i.shortfall)}</span> : '—'}
                </td>
              </tr>
            ))}
            {d.extra.map((e) => (
              <tr key={`extra|${e.name}|${e.unit}`}>
                <td className="px-4 py-2 text-ink-2">{e.name} — нет в спецификации</td>
                <td className="px-3 py-2" />
                <td className="num px-3 py-2 text-right text-ink">
                  {fmt.format(e.received)} {e.unit}
                </td>
                <td className="px-4 py-2" />
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollX>
      {d.receipts.length ? (
        <ul className="divide-y divide-line border-t border-line">
          {d.receipts.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-[13px]">
              <Link
                to="/warehouse/documents/$docId"
                params={{ docId: r.id }}
                className="inline-flex min-h-11 items-center font-medium text-ink underline-offset-4 hover:underline"
              >
                {r.number}
              </Link>
              <span className="text-ink-2">
                {date(r.docDate)} · {r.warehouseName} · строк: {r.lines}
              </span>
              <Badge tone={r.status === 'accepted' ? 'success' : 'neutral'}>
                {r.status === 'accepted' ? 'принято' : 'проведено'}
              </Badge>
            </li>
          ))}
        </ul>
      ) : null}
    </Panel>
  );
}

const num = (v: string | number | null | undefined) => (typeof v === 'number' ? v : 0);

/** Итоги отчёта по позициям: единицы разные, поэтому считаются позиции, а не суммы количеств */
function customerSummary(r: ReportView) {
  const rows = r.rows;
  const left = rows.filter((x) => num(x.onHand) > 1e-9);
  return {
    positions: rows.length,
    left: left.length,
    top: left
      .slice()
      .sort((a, b) => num(b.onHand) - num(a.onHand))
      .slice(0, 3)
      .map((x) => `${String(x.item ?? '—')} — ${fmt.format(num(x.onHand))} ${String(x.unit ?? '')}`.trim()),
  };
}

/** Материалы заказчика по доходному договору: склады заказчика этого проекта и итоги отчёта по каждому */
export function CustomerMaterialsPanel({
  counterpartyId,
  projectId,
}: {
  counterpartyId: string;
  projectId: string;
}) {
  const wh = useQuery({ ...warehousesQuery, retry: false });
  const own = (wh.data ?? []).filter(
    (w: Warehouse) =>
      w.kind === 'customer' &&
      w.counterpartyId === counterpartyId &&
      (w.projectId === null || w.projectId === projectId),
  );
  const reports = useQueries({
    queries: own.map((w) => ({
      queryKey: ['wh', 'report', 'customer', w.id],
      queryFn: () => api<ReportView>(`/warehouse/reports/customer?warehouseId=${w.id}`),
      retry: false,
    })),
  });
  if (wh.isPending || wh.isError || !own.length) return null;
  return (
    <Panel title="Материалы заказчика" meta="давальческие по договору (СК-25)">
      <ul className="divide-y divide-line">
        {own.map((w, i) => {
          const r = reports[i];
          return (
            <li
              key={w.id}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2.5 text-[14px]"
            >
              <Link
                to="/warehouse/warehouses/$warehouseId"
                params={{ warehouseId: w.id }}
                className="inline-flex min-h-11 items-center font-medium text-ink underline-offset-4 hover:underline"
              >
                {w.name}
              </Link>
              {r?.data ? (
                (() => {
                  const sum = customerSummary(r.data);
                  return (
                    <span className="num text-[13px] text-ink-2">
                      позиций {sum.positions} · с остатком{' '}
                      <span className={sum.left > 0 ? 'font-medium text-ink' : undefined}>{sum.left}</span>
                      {sum.top.length ? `: ${sum.top.join('; ')}` : ''}
                    </span>
                  );
                })()
              ) : r?.isError ? (
                <span className="text-[13px] text-ink-2">отчёт недоступен</span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}
